import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Create a global axios instance for non-authenticated calls if needed
const publicApiClient = axios.create({
  baseURL: API_URL,
});

// Create an axios instance for authenticated calls
const apiClient = axios.create({
  baseURL: API_URL,
});

// Request interceptor for apiClient to add the auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for apiClient to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const noRefreshPaths = ["/auth/login", "/auth/register", "/auth/refresh"];

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !noRefreshPaths.some((path) => originalRequest.url.includes(path))
    ) {
      originalRequest._retry = true;
      console.log("[Interceptor] Access token expired. Attempting refresh.");
      const refreshToken = localStorage.getItem("refresh_token");

      if (!refreshToken) {
        console.warn("[Interceptor] No refresh token found. Forcing logout.");
        // Clear any potentially lingering bad tokens
        localStorage.removeItem("access_token");
        // Dispatch a custom event to notify the app to update its state
        window.dispatchEvent(new Event("forceLogout"));
        return Promise.reject(error);
      }

      try {
        const { data } = await publicApiClient.post("/auth/refresh", {
          refresh_token: refreshToken,
        });
        localStorage.setItem("access_token", data.access_token);
        if (data.refresh_token) {
          localStorage.setItem("refresh_token", data.refresh_token);
        }
        console.log("[Interceptor] Token refreshed successfully.");
        // Update the header for the original request and retry it
        originalRequest.headers[
          "Authorization"
        ] = `Bearer ${data.access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error("[Interceptor] Token refresh failed.", refreshError);
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        window.dispatchEvent(new Event("forceLogout"));
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// This object contains all authentication-related API methods
const authService = {
  login: async (email, password) => {
    const formData = new URLSearchParams();
    formData.append("username", email.trim());
    formData.append("password", password);
    // Use the public client for login, as there's no token yet
    const response = await publicApiClient.post("/auth/login", formData, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    if (response.data.access_token) {
      localStorage.setItem("access_token", response.data.access_token);
      localStorage.setItem("refresh_token", response.data.refresh_token);
    }
    return response.data;
  },

  logout: async () => {
    // Get the token BEFORE clearing it, so we can send it to the backend.
    const token = localStorage.getItem("access_token");

    // Step 1: Immediately clear local tokens. This is the crucial fix.
    // It stops any new API calls from using the old, soon-to-be-invalid token.
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    console.log("[authService] Local tokens cleared immediately.");

    // Step 2: Make a "best effort" API call to invalidate the token on the backend.
    // We use a direct axios call to avoid interceptor loops.
    if (token) {
      try {
        await axios.post(
          `${API_URL}/auth/logout`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        console.log("[authService] Backend logout successful.");
      } catch (err) {
        console.warn(
          "[authService] Backend logout API call failed. This is expected if token was already expired or invalid.",
          err.response?.data || err.message
        );
      }
    }
  },

  register: async (email, password) => {
    const response = await publicApiClient.post("/auth/register", {
      email: email.trim(),
      password: password,
    });
    return response.data;
  },

  getCurrentUser: async () => {
    // Uses the authenticated `apiClient` instance
    const response = await apiClient.get("/auth/users/me");
    return response.data;
  },

  isAuthenticated: () => {
    return localStorage.getItem("access_token") !== null;
  },

  // Other methods would also use publicApiClient or apiClient as appropriate
  verifyEmail: async (token) => {
    return await publicApiClient.post("/auth/verify-email", { token });
  },
  requestPasswordReset: async (email) => {
    return await publicApiClient.post("/auth/forgot-password", {
      email: email.trim(),
    });
  },
  resetPassword: async (token, newPassword) => {
    return await publicApiClient.post("/auth/reset-password", {
      token,
      new_password: newPassword,
    });
  },
};

export { apiClient };
export default authService;
